import { Database, Activity, Layers, Zap, Hash, ArrowRight } from "lucide-react";
import { COLORS, ASSETS } from "../styles/tokens";
import { useCountUp } from "../hooks";
import { PACKAGES, ACTIVATION_PATHS } from "../data/packages";
import type { TabId } from "./Header";

interface HeroProps {
  setTab: (tab: TabId) => void;
  totalSignals: number;
  isMobile: boolean;
}

export function Hero({ setTab, totalSignals, isMobile }: HeroProps) {
  const sourcesCount = useCountUp(70);
  const signalsCount = useCountUp(totalSignals);
  const packagesCount = useCountUp(PACKAGES.length);
  const pathsCount = useCountUp(ACTIVATION_PATHS.length);

  const metrics = [
    { label: "Signal sources", value: `${sourcesCount}+`, icon: Database },
    { label: "Active signals", value: signalsCount, icon: Activity },
    { label: "Packages", value: packagesCount, icon: Layers },
    { label: "Activation paths", value: pathsCount, icon: Zap },
  ];

  return (
    <section
      className="grid-bg"
      style={{
        padding: isMobile ? "56px 16px 48px" : "96px 32px 72px",
        borderBottom: `1px solid ${COLORS.border}`,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div className="aurora-1" />
      <div className="aurora-2" />

      <div className="stream-line" style={{ top: "20%", width: "30%", animationDelay: "0s" }} />
      <div className="stream-line" style={{ top: "60%", width: "40%", animationDelay: "1.2s" }} />
      <div className="stream-line" style={{ top: "85%", width: "25%", animationDelay: "2.4s" }} />

      {!isMobile && (
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: "50%",
            right: "-80px",
            transform: "translateY(-50%)",
            width: 520,
            height: 520,
            opacity: 0.07,
            pointerEvents: "none",
            backgroundImage: `url('${ASSETS.badge}')`,
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
          }}
        />
      )}

      <div style={{ maxWidth: 1200, margin: "0 auto", position: "relative" }}>
        <div className="fade-up" style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
          <div style={{ height: 1, width: 32, background: COLORS.cyan, flexShrink: 0 }} />
          <div className="mono" style={{ fontSize: 12, color: COLORS.cyan, letterSpacing: "0.15em" }}>
            INGEST · ENRICH · DELIVER
          </div>
        </div>

        <h1
          className="display fade-up"
          style={{
            fontSize: isMobile ? "clamp(40px, 11vw, 56px)" : "clamp(48px, 7vw, 92px)",
            fontWeight: 800,
            lineHeight: 0.95,
            margin: "0 0 24px",
            maxWidth: 1000,
            animationDelay: "0.1s",
          }}
        >
          Real-world moments,<br />
          <span className="gradient-text">activated as inventory.</span>
        </h1>

        <p
          className="fade-up"
          style={{
            fontSize: isMobile ? 16 : 19,
            color: COLORS.inkDim,
            maxWidth: 640,
            lineHeight: 1.55,
            margin: "0 0 32px",
            animationDelay: "0.2s",
          }}
        >
          RealSynq converts 70+ live data sources into machine-readable intent signals,
          bound to deal IDs and audience keys you can buy in any major DSP. Browse the
          catalog, inspect TTL governance, or onboard a seat.
        </p>

        <div
          className="fade-up"
          style={{
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            animationDelay: "0.3s",
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <button
            onClick={() => setTab("onboard")}
            style={{
              background: COLORS.blue,
              color: "#ffffff",
              border: `1px solid rgba(59, 130, 246, 0.6)`,
              padding: isMobile ? "16px 20px" : "14px 24px",
              fontWeight: 600,
              fontSize: 14,
              borderRadius: 6,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              boxShadow: "0 0 20px rgba(59, 130, 246, 0.3)",
              width: isMobile ? "100%" : "auto",
            }}
          >
            Request seat mapping <ArrowRight size={16} />
          </button>
          <button
            onClick={() => setTab("metadata")}
            style={{
              background: "transparent",
              backdropFilter: "blur(8px)",
              color: COLORS.inkDim,
              border: `1px solid rgba(59, 130, 246, 0.2)`,
              padding: isMobile ? "16px 20px" : "14px 24px",
              fontWeight: 500,
              fontSize: 14,
              borderRadius: 6,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              width: isMobile ? "100%" : "auto",
            }}
          >
            View signal metadata <Hash size={16} />
          </button>
        </div>

        <div
          className="fade-up glass"
          style={{
            marginTop: isMobile ? 48 : 64,
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 1,
            background: `${COLORS.borderHi}40`,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 8,
            overflow: "hidden",
            animationDelay: "0.4s",
          }}
        >
          {metrics.map((m, i) => (
            <div
              key={i}
              style={{
                background: "rgba(10, 19, 32, 0.85)",
                padding: isMobile ? "18px 16px" : "20px 24px",
              }}
            >
              <m.icon size={14} color={COLORS.cyan} style={{ marginBottom: 12 }} />
              <div
                className="display"
                style={{
                  fontSize: isMobile ? 26 : 32,
                  fontWeight: 700,
                  lineHeight: 1,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {m.value}
              </div>
              <div
                className="mono"
                style={{
                  fontSize: 10,
                  color: COLORS.inkMute,
                  letterSpacing: "0.08em",
                  marginTop: 6,
                }}
              >
                {m.label.toUpperCase()}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
